import { useEffect, useRef, useState } from 'react'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

// Client-side cart / wishlist / viewer state for the results grid, mirroring ListingPage's
// List.jsx. On fast pages (SearchPagesController) this data is never in the SSR props - it is
// published client-side through window.pageConfig and the shared cart events, so the ModelCard's
// own add/remove handlers (App.events, /api/internal/*) work unchanged.
const useCartWishlist = () => {
  const [cart, setCart] = useState(EMPTY_OBJECT)
  const [cartItemIds, setCartItemIds] = useState(EMPTY_ARRAY)
  const [wishListItems, setWishListItems] = useState(EMPTY_ARRAY)
  const [currentUserId, setCurrentUserId] = useState(undefined)
  const [viewerIsAdult, setViewerIsAdult] = useState(false)
  const [showCgtVerification, setShowCgtVerification] = useState(false)
  const appliedCartRef = useRef(EMPTY_OBJECT)

  useEffect(() => {
    // Every write here re-renders the whole grid, so the cart and its ids land in one commit with
    // the viewer flags, and the navbar's later re-broadcast of the same cart object is a no-op.
    // Re-applying an unchanged cart would also drop items a card added to cartItemIds since.
    const applyCart = (nextCart) => {
      const cartToApply = nextCart || EMPTY_OBJECT
      if (cartToApply === appliedCartRef.current) {
        return
      }
      appliedCartRef.current = cartToApply
      setCart(cartToApply)
      setCartItemIds(cartToApply.cartItemIds || EMPTY_ARRAY)
    }

    const setCartInfo = (event) => {
      applyCart(event.detail)
    }

    const readPageConfig = () => {
      setCurrentUserId(window?.pageConfig?.currentUserId || undefined)
      setViewerIsAdult(window?.pageConfig?.viewerIsAdult || false)
      setShowCgtVerification(window?.pageConfig?.showCgtVerification || false)
      applyCart(window?.pageConfig?.cart)
    }

    const removeCartItemId = () => {
      const currentCartItemIds = (window?.pageConfig?.cart_items || []).map(({ id }) => id)
      setCartItemIds(state => {
        const itemToBeRemoved = state.find(({ itemId }) => !currentCartItemIds.includes(itemId))
        return state.filter(({ itemId }) => itemId !== itemToBeRemoved?.itemId)
      })
    }

    window.addEventListener('navigation-cart-loaded', setCartInfo)
    window.addEventListener('window-config-loaded', readPageConfig)
    window.addEventListener('item-removed-from-cart', removeCartItemId)

    // React may initialize after the page config / cart already loaded; replay both events.
    if (window?.pageConfig) {
      readPageConfig()
      window.dispatchEvent(new CustomEvent('navigation-cart-loaded', {
        detail: window.pageConfig.cart,
      }))
    }

    return () => {
      window.removeEventListener('navigation-cart-loaded', setCartInfo)
      window.removeEventListener('window-config-loaded', readPageConfig)
      window.removeEventListener('item-removed-from-cart', removeCartItemId)
    }
  }, [])

  useEffect(() => {
    if (!currentUserId) {
      return undefined
    }

    let active = true
    const getWishListItems = async () => {
      try {
        const wishlist = await window.fetchJSON('/api/internal/wish-lists/default')
        // The endpoint serializes wishlist *items* with RecentItemSerializer (type "recentItems"),
        // whose attributes are { id, title, price, url, imageThumb } - there is no itemId, so the
        // long-standing `attributes.itemId` read here always yielded an empty list and no card
        // ever showed as wishlisted on load. Note free items are excluded server-side
        // (WishListItemsController#render_wish_list filters `price != 0`), so they never seed.
        const ids = (wishlist?.data || [])
          .map((item) => Number(item?.attributes?.id))
          .filter(Boolean)
        if (active && ids.length > 0) {
          setWishListItems(ids)
        }
      } catch (error) {
        // Wishlist seeding is best-effort; add/remove still works without the initial list.
      }
    }

    getWishListItems()

    return () => {
      active = false
    }
  }, [currentUserId])

  return {
    cart,
    cartItemIds,
    setCartItemIds,
    wishListItems,
    setWishListItems,
    currentUserId,
    viewerIsAdult,
    setViewerIsAdult,
    showCgtVerification,
  }
}

export default useCartWishlist
